import React, { useCallback, useEffect, useState, Component } from 'react';
import { ArrowLeftIcon, Building2Icon, CalendarIcon, CheckCircle2Icon, CircleIcon, MailIcon, MessageCircleIcon, PencilIcon, PhoneIcon, UserRoundIcon } from 'lucide-react';
import type { AdminProfile } from '../../hooks/useAdminAccess';
import { isAdmin } from '../../lib/crm';
import { ACTIVITY_TYPES, activityTypeLabel, isActivityType } from '../../lib/activities/types';
import { prospectRowFromRecord, type ProspectRow } from '../../lib/prospects/schema';
import { ActivityService } from '../../lib/supabase/services/activities.service';
import { ProspectService } from '../../lib/supabase/services/prospects.service';
import { WorkflowHistoryPanel } from './WorkflowHistoryPanel';
import { WorkflowNextActionCard } from './WorkflowNextActionCard';
type ProspectDetailPageProps = {
  profile: AdminProfile;
  prospectId: string;
};
type RemoteRecord = Record<string, unknown>;
type Prospect = {
  id: string;
  businessName: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  city: string | null;
  province: string | null;
  address: string | null;
  website: string | null;
  instagram: string | null;
  facebook: string | null;
  googleMaps: string | null;
  nicheId: string | null;
  sourceId: string | null;
  interestLevel: string | null;
  notes: string | null;
  owner: string | null;
  createdAt: string | null;
};
type Activity = {
  id: string;
  type: string;
  description: string;
  createdAt: string | null;
  author: string | null;
};
type DetailTab = 'activity' | 'workflow';
export function ProspectDetailPage({
  profile,
  prospectId
}: ProspectDetailPageProps) {
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activeTab, setActiveTab] = useState<DetailTab>('activity');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activityError, setActivityError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const loadActivities = useCallback(async () => {
    try {
      const rows = await ActivityService.listForProspect(prospectId);
      setActivities(rows.map(row => mapActivity(row as RemoteRecord)));
      setActivityError('');
    } catch (loadError) {
      setActivities([]);
      setActivityError(loadError instanceof Error ? loadError.message : 'No se pudo cargar la actividad.');
    }
  }, [prospectId]);
  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const result = await ProspectService.get(prospectId);
      if (!result) {
        setProspect(null);
        setError(`No se encontró el prospecto solicitado. UUID: ${prospectId}`);
        return;
      }
      setProspect(mapProspect(result as RemoteRecord));
      await loadActivities();
    } catch (loadError) {
      setProspect(null);
      setError(loadError instanceof Error ? loadError.message : 'No se pudo cargar la ficha del prospecto.');
    } finally {
      setIsLoading(false);
    }
  }, [loadActivities, prospectId]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  async function refreshWorkflowContext() {
    await refresh();
    setRefreshKey(current => current + 1);
  }
  if (isLoading) {
    return <main className="mx-auto min-w-0 max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-id="element-381">
        <p className="text-sm text-slate-400" data-id="element-382">Cargando ficha del prospecto…</p>
      </main>;
  }
  if (error || !prospect) {
    return <main className="mx-auto min-w-0 max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-id="element-383">
        <button className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-200 hover:text-cyan-100" onClick={() => navigate('/admin/prospectos')} type="button" data-id="element-384">
          <ArrowLeftIcon className="h-4 w-4" data-id="element-385" />
          Volver a prospectos
        </button>
        <section className="mt-6 rounded-2xl border border-rose-400/20 bg-rose-400/10 p-5 text-sm text-rose-100" role="alert" data-id="element-386">
          {error || 'Este prospecto no está disponible.'}
        </section>
      </main>;
  }
  return <main className="mx-auto min-w-0 max-w-7xl px-4 py-7 sm:px-6 lg:px-8" data-id="element-387">
      <button className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-200 hover:text-cyan-100" onClick={() => navigate('/admin/prospectos')} type="button" data-id="element-388">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-389" />
        Volver a prospectos
      </button>

      <header className="mt-5 flex flex-col gap-5 border-b border-white/10 pb-6 lg:flex-row lg:items-start lg:justify-between" data-id="element-390">
        <div className="min-w-0" data-id="element-391">
          <p className="font-orbitron text-xs font-bold tracking-[.18em] text-cyan-200" data-id="element-392">
            FICHA DEL PROSPECTO
          </p>
          <h1 className="mt-2 truncate text-3xl font-bold text-white" data-id="element-393">
            {prospect.businessName}
          </h1>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-400" data-id="element-394">
            {prospect.contactName ? <span className="inline-flex items-center gap-2" data-id="element-395">
                <UserRoundIcon className="h-4 w-4 text-cyan-200" data-id="element-396" />
                {prospect.contactName}
              </span> : null}
            {prospect.city ? <span className="inline-flex items-center gap-2" data-id="element-397">
                <Building2Icon className="h-4 w-4 text-cyan-200" data-id="element-398" />
                {prospect.city}
              </span> : null}
            {prospect.owner ? <span data-id="element-399">Responsable: {prospect.owner}</span> : null}
          </div>
        </div>

        {isAdmin(profile.role) ? <button className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-cyan-300/30 px-4 text-sm font-bold text-cyan-100 hover:bg-cyan-300/10" onClick={() => navigate(`/admin/prospectos/${prospectId}/editar`)} type="button" data-id="element-400">
            <PencilIcon className="h-4 w-4" data-id="element-401" />
            Editar prospecto
          </button> : null}
      </header>

      <section aria-label="Datos de contacto" className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-402">
        <ContactItem icon={<PhoneIcon className="h-4 w-4" data-id="element-404" />} label="Teléfono" value={prospect.phone} data-id="element-403" />
        <ContactItem icon={<MessageCircleIcon className="h-4 w-4" data-id="element-406" />} label="WhatsApp" value={prospect.whatsapp} data-id="element-405" />
        <ContactItem icon={<MailIcon className="h-4 w-4" data-id="element-408" />} label="Correo" value={prospect.email} data-id="element-407" />
        <ContactItem icon={<CalendarIcon className="h-4 w-4" data-id="element-410" />} label="Registrado" value={formatDate(prospect.createdAt)} data-id="element-409" />
      </section>

      <div className="mt-6" data-id="element-411">
        <WorkflowNextActionCard onRefresh={refreshWorkflowContext} onUpdateInformation={() => navigate(`/admin/prospectos/${prospectId}/editar`)} prospect={prospect} prospectId={prospectId} data-id="element-412" />
      </div>

      <KnownInformationPanel onUpdate={() => navigate(`/admin/prospectos/${prospectId}/editar`)} prospect={prospect} data-id="element-413" />

      <section className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-[#0b1728]" data-id="element-414">
        <div aria-label="Detalle del prospecto" className="flex border-b border-white/10" role="tablist" data-id="element-415">
          <TabButton active={activeTab === 'activity'} id="activity" label="Actividad" onClick={() => setActiveTab('activity')} data-id="element-416" />
          <TabButton active={activeTab === 'workflow'} id="workflow" label="Historial del proceso" onClick={() => setActiveTab('workflow')} data-id="element-417" />
        </div>

        <div className="p-5 sm:p-6" data-id="element-418">
          {activeTab === 'activity' ? <ActivityTimeline activities={activities} error={activityError} data-id="element-419" /> : <WorkflowHistoryPanel prospectId={prospectId} refreshKey={refreshKey} data-id="element-420" />}
        </div>
      </section>
    </main>;
}
function KnownInformationPanel({
  prospect,
  onUpdate
}: {
  prospect: Prospect;
  onUpdate: () => void;
}) {
  const fields: Array<{
    label: string;
    available: boolean;
  }> = [{
    label: 'Nombre del negocio',
    available: Boolean(prospect.businessName)
  }, {
    label: 'Persona de contacto',
    available: Boolean(prospect.contactName)
  }, {
    label: 'Teléfono o WhatsApp',
    available: Boolean(prospect.phone || prospect.whatsapp)
  }, {
    label: 'Correo electrónico',
    available: Boolean(prospect.email)
  }, {
    label: 'Ubicación',
    available: Boolean(prospect.city || prospect.province || prospect.address)
  }, {
    label: 'Canales digitales',
    available: Boolean(prospect.website || prospect.instagram || prospect.facebook || prospect.googleMaps)
  }];
  return <section aria-labelledby="known-information-title" className="mt-6 rounded-2xl border border-white/10 bg-[#0b1728] p-5" data-id="element-421">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-422">
        <div data-id="element-423">
          <h2 className="text-lg font-bold text-white" id="known-information-title" data-id="element-424">
            Información conocida
          </h2>
          <p className="mt-1 text-sm text-slate-400" data-id="element-425">
            No impide continuar el proceso comercial.
          </p>
        </div>
        <button className="inline-flex min-h-10 items-center text-sm font-semibold text-cyan-200 underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" onClick={onUpdate} type="button" data-id="element-426">
          Actualizar información
        </button>
      </div>
      <ul className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-id="element-427">
        {fields.map(field => <li className="flex items-center gap-2 text-sm text-slate-300" key={field.label} data-id="element-428">
            {field.available ? <CheckCircle2Icon className="h-4 w-4 shrink-0 text-emerald-300" data-id="element-429" /> : <CircleIcon className="h-4 w-4 shrink-0 text-slate-600" data-id="element-430" />}
            <span data-id="element-431">{field.label}</span>
            {!field.available ? <span className="text-xs text-slate-500" data-id="element-432">Sin dato</span> : null}
          </li>)}
      </ul>
    </section>;
}
function ContactItem({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null;
}) {
  return <div className="min-w-0 rounded-xl border border-white/10 bg-[#0b1728] p-3.5" data-id="element-433">
      <p className="flex items-center gap-2 text-xs font-medium text-slate-400" data-id="element-434">
        <span className="text-cyan-200" data-id="element-435">{icon}</span>
        {label}
      </p>
      <p className="mt-1.5 truncate text-sm font-semibold text-slate-100" data-id="element-436">
        {value || 'No disponible'}
      </p>
    </div>;
}
function TabButton({
  active,
  id,
  label,
  onClick
}: {
  active: boolean;
  id: DetailTab;
  label: string;
  onClick: () => void;
}) {
  return <button aria-controls={`${id}-panel`} aria-selected={active} className={`min-h-12 px-4 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-300 ${active ? 'border-b-2 border-cyan-300 text-cyan-100' : 'text-slate-400 hover:text-slate-100'}`} id={id} onClick={onClick} role="tab" type="button" data-id="element-437">
      {label}
    </button>;
}
function ActivityTimeline({
  activities,
  error
}: {
  activities: Activity[];
  error: string;
}) {
  return <div aria-labelledby="activity" id="activity-panel" role="tabpanel" data-id="element-438">
      <div className="flex items-center justify-between gap-3" data-id="element-439">
        <div data-id="element-440">
          <h2 className="text-lg font-bold text-white" data-id="element-441">Actividad</h2>
          <p className="mt-1 text-sm text-slate-400" data-id="element-442">
            Trazabilidad de contactos y gestiones registradas.
          </p>
        </div>
        <span className="rounded-full bg-cyan-300/10 px-2.5 py-1 text-xs font-semibold text-cyan-100" data-id="element-443">
          {activities.length}
        </span>
      </div>

      {error ? <p className="mt-5 rounded-xl border border-amber-300/20 bg-amber-300/10 p-3 text-sm text-amber-100" role="alert" data-id="element-444">
          No se pudo cargar la actividad. {error}
        </p> : null}

      {activities.length === 0 ? <p className="mt-6 rounded-xl border border-dashed border-white/15 p-4 text-sm text-slate-400" data-id="element-445">
          Todavía no hay actividades para este prospecto.
        </p> : <ol className="mt-6 space-y-5 border-l border-cyan-300/20 pl-5" data-id="element-446">
          {activities.map(activity => <li className="relative min-w-0" key={activity.id} data-id="element-447">
              <span aria-hidden="true" className="absolute -left-[1.6rem] top-1.5 h-2.5 w-2.5 rounded-full bg-cyan-300" data-id="element-448" />
              <p className="text-sm font-semibold text-cyan-100" data-id="element-449">
                {activityLabel(activity.type)}
              </p>
              <p className="mt-1 break-words text-sm leading-6 text-slate-200" data-id="element-450">
                {activity.description}
              </p>
              <p className="mt-1.5 text-xs text-slate-500" data-id="element-451">
                {activity.author || 'Equipo'} · {formatDate(activity.createdAt)}
              </p>
            </li>)}
        </ol>}
    </div>;
}
function mapProspect(record: RemoteRecord): Prospect {
  const row: ProspectRow = prospectRowFromRecord(record);
  return {
    id: row.id,
    businessName: row.business_name || 'Prospecto sin nombre',
    contactName: row.contact_name,
    email: row.email,
    phone: row.phone,
    whatsapp: row.whatsapp,
    city: row.city,
    province: row.province,
    address: row.address,
    website: row.website_url,
    instagram: row.instagram_url,
    facebook: row.facebook_url,
    googleMaps: row.google_maps_url,
    nicheId: row.niche_id,
    sourceId: row.source_id,
    interestLevel: row.interest_level,
    notes: row.notes,
    owner: nullableString(record.assigned_to_name, record.responsible_name, record.owner_name),
    createdAt: row.created_at
  };
}
function mapActivity(record: RemoteRecord): Activity {
  return {
    id: stringValue(record.id, record.activity_id) || `${Date.now()}-${Math.random()}`,
    type: resolveActivityType(record.activity_type, record.type),
    description: stringValue(record.description) || 'Actividad registrada.',
    createdAt: nullableString(record.created_at, record.occurred_at, record.activity_at),
    author: nullableString(record.created_by_name, record.user_name, record.author_name)
  };
}
function stringValue(...values: unknown[]) {
  const item = values.find(value => typeof value === 'string' && value.trim());
  return typeof item === 'string' ? item : '';
}
function nullableString(...values: unknown[]) {
  return stringValue(...values) || null;
}
function formatDate(value: string | null) {
  if (!value) return 'No disponible';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No disponible';
  return new Intl.DateTimeFormat('es-CR', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
}
function resolveActivityType(...values: unknown[]) {
  const value = stringValue(...values);
  return isActivityType(value) ? value : ACTIVITY_TYPES.OTHER;
}
function activityLabel(type: string) {
  return activityTypeLabel(type);
}
function navigate(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}